import type { DocumentListRecord } from "../types";
import { registerState } from "./invoiceState";

/**
 * The register's filtering, sorting, grouping and money arithmetic, as pure
 * functions over the list the API already returned.
 *
 * Everything here runs client-side. `listDocuments()` returns the whole
 * register in one request for one office, so filtering on the server would buy
 * a query API, pagination state and a summary strip that could no longer be
 * computed from what the page is holding. The horizon is response time, not a
 * row count: when that one request gets slow, this is the thing to revisit.
 */

export type KindFilter = "all" | "invoice" | "quote";
/** "outstanding" is the working filter: sent invoices with money still owed. */
export type StatusFilter =
	| "all"
	| "outstanding"
	| "draft"
	| "sent"
	| "part-paid"
	| "paid"
	| "accepted"
	| "expired";
export type SortKey = "newest" | "oldest" | "balance" | "total" | "client" | "number";

export interface RegisterFilters {
	search: string;
	kind: KindFilter;
	status: StatusFilter;
}

export const DEFAULT_FILTERS: RegisterFilters = {
	search: "",
	kind: "all",
	status: "all",
};

/** True when anything is narrowing the table, which the strip's caption says. */
export function isFiltered(filters: RegisterFilters): boolean {
	return (
		filters.search.trim() !== "" || filters.kind !== "all" || filters.status !== "all"
	);
}

/** The date a row is shown and sorted by: when it went out, else when it was written. */
export function documentDate(document: DocumentListRecord): string {
	return document.sentAt ?? document.createdAt;
}

function matchesSearch(document: DocumentListRecord, needle: string): boolean {
	if (!needle) return true;
	const haystack = [
		document.number,
		document.clientName,
		document.propertyAddress,
		document.jobRefInternal,
	]
		.join(" ")
		.toLowerCase();
	return haystack.includes(needle);
}

function matchesStatus(document: DocumentListRecord, status: StatusFilter): boolean {
	if (status === "all") return true;
	const state = registerState(document);
	if (status === "outstanding") {
		return document.kind === "invoice" && (state === "sent" || state === "part-paid");
	}
	return state === status;
}

export function filterDocuments(
	documents: DocumentListRecord[],
	filters: RegisterFilters,
): DocumentListRecord[] {
	const needle = filters.search.trim().toLowerCase();
	return documents.filter(
		(document) =>
			(filters.kind === "all" || document.kind === filters.kind) &&
			matchesStatus(document, filters.status) &&
			matchesSearch(document, needle),
	);
}

/**
 * Sorting returns a new array — the caller's list is the server's order, which
 * "Newest first" is defined as, so it must survive being sorted away and back.
 */
export function sortDocuments(
	documents: DocumentListRecord[],
	sort: SortKey,
): DocumentListRecord[] {
	const rows = [...documents];
	switch (sort) {
		case "oldest":
			return rows.sort((a, b) => documentDate(a).localeCompare(documentDate(b)));
		case "balance":
			return rows.sort((a, b) => (b.balanceCents ?? -1) - (a.balanceCents ?? -1));
		case "total":
			return rows.sort((a, b) => b.totalCents - a.totalCents);
		case "client":
			return rows.sort((a, b) =>
				a.clientName.localeCompare(b.clientName, "en-AU", { sensitivity: "base" }),
			);
		case "number":
			return rows.sort((a, b) =>
				a.number.localeCompare(b.number, "en-AU", { numeric: true }),
			);
		default:
			return rows;
	}
}

export interface DocumentGroup {
	/** null for an invoice that belongs to no job — it stands alone. */
	jobId: string | null;
	key: string;
	jobRefInternal: string;
	propertyAddress: string;
	clientName: string;
	documents: DocumentListRecord[];
}

/**
 * Groups by the canonical job id, so legacy blank references stay distinct. An
 * invoice with no job has nothing to group with and heads its own group.
 */
export function groupDocuments(documents: DocumentListRecord[]): DocumentGroup[] {
	const groupsByKey = new Map<string, DocumentGroup>();
	for (const document of documents) {
		const key = document.jobId ?? `standalone:${document.kind}:${document.id}`;
		const existing = groupsByKey.get(key);
		if (existing) {
			existing.documents.push(document);
			continue;
		}
		groupsByKey.set(key, {
			jobId: document.jobId,
			key,
			jobRefInternal: document.jobRefInternal,
			propertyAddress: document.propertyAddress,
			clientName: document.clientName,
			documents: [document],
		});
	}
	return [...groupsByKey.values()];
}

export interface RegisterTotals {
	outstandingCents: number;
	awaitingCount: number;
	partPaidCount: number;
	receivedCents: number;
	settledCount: number;
	draftCents: number;
	draftCount: number;
	invoicedCents: number;
	issuedCount: number;
}

/**
 * The money strip's figures, over invoices only — a quote is an offer, not an
 * amount owed. Deliberately arranged so the arithmetic is checkable by eye:
 * Outstanding + Received = Invoiced.
 */
export function summariseInvoices(documents: DocumentListRecord[]): RegisterTotals {
	const totals: RegisterTotals = {
		outstandingCents: 0,
		awaitingCount: 0,
		partPaidCount: 0,
		receivedCents: 0,
		settledCount: 0,
		draftCents: 0,
		draftCount: 0,
		invoicedCents: 0,
		issuedCount: 0,
	};

	for (const document of documents) {
		if (document.kind !== "invoice") continue;
		totals.receivedCents += document.paidCents ?? 0;

		if (document.status === "draft") {
			totals.draftCents += document.totalCents;
			totals.draftCount += 1;
			continue;
		}

		totals.invoicedCents += document.totalCents;
		totals.issuedCount += 1;

		if (document.status === "paid") {
			totals.settledCount += 1;
			continue;
		}
		if (document.status === "sent") {
			totals.outstandingCents += document.balanceCents ?? document.totalCents;
			totals.awaitingCount += 1;
			if ((document.paidCents ?? 0) > 0) totals.partPaidCount += 1;
		}
	}

	return totals;
}
